import React, { useState } from 'react';
import type { Tache, AppData } from '../types';
import { Button, Card, Input, Select } from './UI';

interface TachesListProps {
  data: AppData;
  onDataChange: (data: AppData) => void;
}

export function TachesList({ data, onDataChange }: TachesListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Tache>>({});
  const [filterStatus, setFilterStatus] = useState<'all' | 'done' | 'todo'>('todo');

  const dossiers = data.dossiers;
  let taches = data.taches.sort((a, b) => {
    const priorityOrder = { 'Haute': 0, 'Normale': 1, 'Basse': 2 };
    return priorityOrder[a.priorite] - priorityOrder[b.priorite];
  });

  if (filterStatus === 'done') {
    taches = taches.filter((t) => t.fait);
  } else if (filterStatus === 'todo') {
    taches = taches.filter((t) => !t.fait);
  }

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      id: `t${Date.now()}`,
      dossier: dossiers[0]?.id || '',
      titre: '',
      fait: false,
      priorite: 'Normale',
      date: new Date().toISOString().split('T')[0],
    });
  };

  const handleEdit = (tache: Tache) => {
    setEditingId(tache.id);
    setFormData(tache);
  };

  const handleToggleDone = (tache: Tache) => {
    const newData = { ...data };
    const updatedTache = { ...tache, fait: !tache.fait };
    newData.taches = newData.taches.map((t) => (t.id === tache.id ? updatedTache : t));
    newData.journal.push({
      id: `j${Date.now()}`,
      dossier: tache.dossier,
      action: tache.fait ? 'Tâche réactivée' : 'Tâche complétée',
      detail: tache.titre,
      ts: new Date().toISOString(),
    });
    onDataChange(newData);
  };

  const handleSave = () => {
    if (!formData.dossier || !formData.titre || !formData.date) {
      alert('Veuillez remplir les champs requis');
      return;
    }

    const newData = { ...data };

    if (isCreating) {
      newData.taches.push(formData as Tache);
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Tâche créée',
        detail: formData.titre!,
        ts: new Date().toISOString(),
      });
    } else {
      newData.taches = newData.taches.map((t) =>
        t.id === editingId ? (formData as Tache) : t
      );
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Tâche modifiée',
        detail: formData.titre!,
        ts: new Date().toISOString(),
      });
    }

    onDataChange(newData);
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')) return;

    const tache = data.taches.find((t) => t.id === id);
    const newData = { ...data };
    newData.taches = newData.taches.filter((t) => t.id !== id);
    newData.journal.push({
      id: `j${Date.now()}`,
      dossier: tache?.dossier || '',
      action: 'Tâche supprimée',
      detail: tache?.titre || '',
      ts: new Date().toISOString(),
    });

    onDataChange(newData);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  if (isCreating || editingId) {
    const dossier = dossiers.find((d) => d.id === formData.dossier);

    return (
      <div>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>
          {isCreating ? 'Créer une tâche' : 'Éditer la tâche'}
        </h1>

        <Card style={{ maxWidth: 600 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Dossier *
              </label>
              <Select
                value={formData.dossier || ''}
                onChange={(e) => setFormData({ ...formData, dossier: e.target.value })}
              >
                <option value="">Sélectionner un dossier</option>
                {dossiers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.ref} — {d.client}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Description *
              </label>
              <Input
                value={formData.titre || ''}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Ex: Relancer le client pour pièces manquantes"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Date d'échéance *
                </label>
                <Input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Priorité *
                </label>
                <Select
                  value={formData.priorite || 'Normale'}
                  onChange={(e) => setFormData({ ...formData, priorite: e.target.value as any })}
                >
                  <option value="Haute">🔴 Haute</option>
                  <option value="Normale">🟡 Normale</option>
                  <option value="Basse">🟢 Basse</option>
                </Select>
              </div>
            </div>

            {dossier && (
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: 'var(--border-radius-md)', fontSize: 12 }}>
                <strong>{dossier.ref}</strong> — {dossier.client}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} variant="secondary">
                Annuler
              </Button>
              <Button onClick={handleSave}>
                {isCreating ? 'Créer' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const stats = {
    total: data.taches.length,
    done: data.taches.filter((t) => t.fait).length,
    todo: data.taches.filter((t) => !t.fait).length,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Tâches</h1>
        <Button onClick={handleCreate}>+ Ajouter une tâche</Button>
      </div>

      {/* Stats and filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setFilterStatus('todo')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: filterStatus === 'todo' ? 'var(--color-accent)' : 'var(--color-background-secondary)',
            color: filterStatus === 'todo' ? '#ffffff' : 'var(--color-text-primary)',
            border: 'none',
            borderRadius: 'var(--border-radius-md)',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          À faire ({stats.todo})
        </button>
        <button
          onClick={() => setFilterStatus('done')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: filterStatus === 'done' ? 'var(--color-accent)' : 'var(--color-background-secondary)',
            color: filterStatus === 'done' ? '#ffffff' : 'var(--color-text-primary)',
            border: 'none',
            borderRadius: 'var(--border-radius-md)',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Complétées ({stats.done})
        </button>
        <button
          onClick={() => setFilterStatus('all')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: filterStatus === 'all' ? 'var(--color-accent)' : 'var(--color-background-secondary)',
            color: filterStatus === 'all' ? '#ffffff' : 'var(--color-text-primary)',
            border: 'none',
            borderRadius: 'var(--border-radius-md)',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Tout ({stats.total})
        </button>
      </div>

      {taches.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {taches.map((tache) => {
            const dossier = dossiers.find((d) => d.id === tache.dossier);
            const priorityColors = { 'Haute': '#cf222e', 'Normale': '#9e6a03', 'Basse': '#1a7f37' };
            return (
              <Card key={tache.id} style={{ opacity: tache.fait ? 0.6 : 1 }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'start' }}>
                  <input
                    type="checkbox"
                    checked={tache.fait}
                    onChange={() => handleToggleDone(tache)}
                    style={{ marginTop: '2px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 0.25rem', fontSize: 13, fontWeight: 500, textDecoration: tache.fait ? 'line-through' : 'none' }}>
                      {tache.titre}
                    </p>
                    <p style={{ margin: '0 0 0.5rem', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      {dossier?.ref} — {dossier?.client}
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                      {tache.date}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span
                      style={{
                        background: priorityColors[tache.priorite],
                        color: '#ffffff',
                        padding: '2px 8px',
                        borderRadius: 'var(--border-radius-sm)',
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                    >
                      {tache.priorite}
                    </span>
                    <Button onClick={() => handleEdit(tache)} size="sm" variant="secondary">
                      ✎
                    </Button>
                    <Button onClick={() => handleDelete(tache.id)} size="sm" variant="secondary">
                      🗑
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            {filterStatus === 'todo' ? 'Aucune tâche à faire' : filterStatus === 'done' ? 'Aucune tâche complétée' : 'Aucune tâche enregistrée'}
          </p>
          <Button onClick={handleCreate}>Créer la première tâche</Button>
        </Card>
      )}
    </div>
  );
}
